import type { FieldValues } from 'react-hook-form';
import StatusMessage from 'creative-ui/uikit/StatusMessage';
import { WidgetInstance } from 'friendly-challenge';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

import { getCaptchaTheme } from '@/utils/theme/themeEngine';

import type { CaptchaResponse, CaptchaWidgetProps } from '../index';

const FRIENDLY_CAPTCHA_PROVIDER = 'friendly_captcha';

const FriendlyCaptchaWidget = <T extends FieldValues = FieldValues>({
  config,
  onCaptchaResponse,
  control,
  name,
  rules,
  setError,
  clearErrors,
  error,
  theme,
}: CaptchaWidgetProps<T>) => {
  const [isClient, setIsClient] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<WidgetInstance | null>(null);
  const onChangeRef = useRef<((value: string) => void) | null>(null);

  const siteKey = config.siteKey;
  const isFriendlyCaptcha = config.provider === FRIENDLY_CAPTCHA_PROVIDER;

  // Sync external Auth0 server error into react-hook-form
  useEffect(() => {
    if (error && setError) {
      setError(name, { type: 'server', message: error });
    }
  }, [error, name, setError]);

  // Client-only rendering
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Initialize the widget
  useEffect(() => {
    if (
      !isClient ||
      !isFriendlyCaptcha ||
      !siteKey ||
      !containerRef.current ||
      widgetRef.current
    ) {
      return;
    }

    widgetRef.current = new WidgetInstance(containerRef.current, {
      sitekey: siteKey,
      doneCallback: (solution: string) => {
        onChangeRef.current?.(solution);
        clearErrors?.(name);
        const response: CaptchaResponse = {
          provider: FRIENDLY_CAPTCHA_PROVIDER,
          token: solution,
        };
        onCaptchaResponse(response);
      },
      errorCallback: () => {
        onChangeRef.current?.('');
        onCaptchaResponse(null);
      },
    });
  }, [
    isClient,
    isFriendlyCaptcha,
    siteKey,
    name,
    clearErrors,
    onCaptchaResponse,
  ]);

  if (!isClient || !isFriendlyCaptcha || !siteKey) {
    return null;
  }

  const friendlyCaptchaWidgetStyle = {
    transformOrigin: '0 0',
    overflow: 'hidden',
    display: 'inline-block',
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange }, fieldState }) => {
        // Store onChange in ref so widget callbacks can access it
        onChangeRef.current = onChange;

        return (
          <div>
            <div style={friendlyCaptchaWidgetStyle}>
              <div
                data-testid="friendly-captcha-container"
                ref={containerRef}
                className={`frc-captcha ${getCaptchaTheme(theme)}`}
                data-sitekey={siteKey}
              />
            </div>
            {fieldState.error?.message && (
              <StatusMessage className="u-margin-t-xs" mode="error">
                {fieldState.error.message}
              </StatusMessage>
            )}
          </div>
        );
      }}
    />
  );
};

export default FriendlyCaptchaWidget;
